import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authService } from '../services';
import { handleApiError, showErrorNotification, showSuccessNotification } from '../utils/errorHandler';

export interface LoginResult {
  success: boolean;
  statusCode?: number;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Check if user is already logged in on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        
        // Check if token exists in cookie or localStorage
        if (authService.isAuthenticated()) {
          const storedUser = authService.getStoredUser();
          
          // If we have stored user data, set it immediately for better UX
          if (storedUser) {
            setUser(storedUser);
          }
          
          // Always validate token with backend on page load
          try {
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
          } catch (error) {
            console.error('Token validation failed:', error);
            // Token is invalid or expired, clear everything and redirect
            authService.logout();
            setUser(null);
            // Redirect will be handled by the route protection
          }
        } else {
          // No token found, ensure user is null
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // On any error, clear auth state
        authService.logout();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await authService.login({ email, password });

      const userData: User = {
        id: response.data.user.id,
        name: response.data.user.fullName || response.data.user.username || response.data.user.email,
        email: response.data.user.email,
        status: 'online',
        avatar: undefined,
        roles: response.data.user.roles || [],
      };

      setUser(userData);
      showSuccessNotification(response.message || 'Login successful!');
      return { success: true };
    } catch (error) {
      const apiError = handleApiError(error);
      showErrorNotification(apiError);
      console.error('Login failed:', apiError.message);
      return { success: false, statusCode: apiError.statusCode };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      showSuccessNotification('Logged out successfully');
    } catch (error) {
      const apiError = handleApiError(error);
      showErrorNotification(apiError);
      console.error('Logout failed:', apiError.message);
      // Clear local state even if API call fails
      setUser(null);
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      await authService.changePassword({ currentPassword, newPassword });
      showSuccessNotification('Password changed successfully!');
      return true;
    } catch (error) {
      const apiError = handleApiError(error);
      showErrorNotification(apiError);
      console.error('Password change failed:', apiError.message);
      return false;
    }
  };

  const value = {
    user,
    login,
    logout,
    changePassword,
    isAuthenticated: !!user,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
